// components/ShippingLabelModal.jsx
// Create a UPS label for a POS order or product-sample request. Parses the
// order's ship-to into editable fields (UPS rejects malformed addresses), lets
// the user pick service/weight/dimensions/format (defaults from the tenant's
// carrier settings), then calls the create-shipping-label edge function. On
// success it shows the tracking number + a label download link and (optionally)
// a void action. Used from POSShippingModal and ShippingEditModal.
import React, { useEffect, useMemo, useState } from "react";
import { loadShippingCarrier, createShippingLabel, voidShippingLabel } from "../services/posService";
import { isShipBlocked, shipBlockedMessage } from "../utils/approval.js";
import "./ShippingLabelModal.css";

const SERVICE_CODES = [
  { code: "03", label: "Ground" },
  { code: "02", label: "2nd Day Air" },
  { code: "12", label: "3 Day Select" },
  { code: "13", label: "Next Day Air Saver" },
  { code: "01", label: "Next Day Air" },
  { code: "14", label: "Next Day Air Early" },
  { code: "59", label: "2nd Day Air A.M." },
];

// Best-effort parse of a one-line US address — mirrors the edge function so the
// user sees the same starting point and can correct it before submitting.
function parseAddress(line, fallbackState = "") {
  const parts = String(line || "").split(",").map((p) => p.trim()).filter(Boolean);
  let street = "", city = "", state = fallbackState, postal = "";
  if (parts.length >= 3) {
    street = parts.slice(0, parts.length - 2).join(", ");
    city = parts[parts.length - 2];
    const m = parts[parts.length - 1].match(/([A-Za-z]{2})\s*(\d{5}(?:-\d{4})?)?/);
    if (m) { state = m[1].toUpperCase(); postal = m[2] || ""; }
  } else {
    street = parts[0] || "";
    const m = (parts[1] || "").match(/(.*?)\s+([A-Za-z]{2})\s*(\d{5}(?:-\d{4})?)?$/);
    if (m) { city = m[1]; state = m[2].toUpperCase(); postal = m[3] || ""; }
    else city = parts[1] || "";
  }
  return { street, city, state, postal };
}

export default function ShippingLabelModal({ order, orderType, onClose, onLabeled }) {
  // Defence in depth for the sample approval gate (migration 0097). The opener
  // (ShippingEditModal) already hides the Create-label button on a blocked row,
  // so this should be unreachable — but "Mark as Shipped" defaults ON and the
  // label is bought from UPS BEFORE the status write, so if it were ever reached
  // the tenant would pay postage for a shipment the database then refuses. POS
  // orders have no approval concept and are unaffected.
  const shipGated = orderType === "sample" && isShipBlocked(order);

  const initialShipTo = useMemo(() => {
    if (orderType === "sample") {
      const a = parseAddress(order.address, order.state);
      return { name: order.shipToCompany || order.contactName || "", phone: order.contactPhone || "", ...a };
    }
    const a = parseAddress(order.shipToAddress, "");
    return { name: order.shipToCompany || order.shipToContactName || "", phone: order.shipToContactPhone || "", ...a };
  }, [order, orderType]);

  const [shipTo, setShipTo] = useState(initialShipTo);
  const [serviceCode, setServiceCode] = useState("03");
  const [labelFormat, setLabelFormat] = useState("PDF");
  const [weight, setWeight] = useState("1");
  const [dims, setDims] = useState({ length: "", width: "", height: "" });
  const [markShipped, setMarkShipped] = useState(!shipGated);

  const [carrier, setCarrier] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { shipmentId, trackingNumber, labelUrl, totalCharge, currency }

  useEffect(() => {
    loadShippingCarrier("ups")
      .then((c) => {
        setCarrier(c);
        if (c) {
          setServiceCode(c.default_service_code || "03");
          setLabelFormat(c.default_label_format || "PDF");
          setWeight(String(c.default_weight_lbs ?? "1"));
          setDims({
            length: c.default_length_in != null ? String(c.default_length_in) : "",
            width: c.default_width_in != null ? String(c.default_width_in) : "",
            height: c.default_height_in != null ? String(c.default_height_in) : "",
          });
        }
      })
      .finally(() => setLoadingCfg(false));
  }, []);

  const setST = (k, v) => setShipTo((p) => ({ ...p, [k]: v }));
  const setD = (k, v) => setDims((p) => ({ ...p, [k]: v }));

  const ready = shipTo.street && shipTo.city && shipTo.state && shipTo.postal;

  async function handleCreate() {
    setError(null);
    if (!ready) { setError("Fill in street, city, state, and ZIP."); return; }
    if (shipGated) { setError(shipBlockedMessage(order)); return; }
    setBusy(true);
    try {
      const res = await createShippingLabel({
        orderType,
        orderId: order.id,
        shipTo,
        serviceCode,
        weightLbs: weight,
        length: dims.length || undefined,
        width: dims.width || undefined,
        height: dims.height || undefined,
        labelFormat,
        markShipped,
      });
      setResult(res);
      onLabeled?.({
        carrier: "UPS",
        tracking: res.trackingNumber,
        shipDate: new Date().toISOString().slice(0, 10),
        ...(markShipped ? { status: "Shipped" } : {}),
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleVoid() {
    if (!result?.shipmentId) return;
    if (!confirm("Void this label? The tracking number will be cleared.")) return;
    setBusy(true);
    setError(null);
    try {
      await voidShippingLabel(result.shipmentId);
      onLabeled?.({ carrier: "UPS", tracking: "", shipDate: "" });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="slm-backdrop" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="slm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="slm-header">
          <div className="slm-title">
            <span className="slm-badge">UPS</span>
            Create Shipping Label
            {carrier?.environment === "sandbox" && <span className="slm-env">Sandbox</span>}
          </div>
          <button className="slm-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="slm-body">
          {loadingCfg ? (
            <div className="slm-muted">Loading carrier settings…</div>
          ) : !carrier?.enabled || !carrier?.has_credentials ? (
            <div className="slm-warn">
              UPS isn’t connected yet. A workspace admin can set it up in
              <strong> System Settings → Shipping Carriers</strong>.
            </div>
          ) : result ? (
            <div className="slm-success">
              <div className="slm-success-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </div>
              <div className="slm-success-title">Label created</div>
              <div className="slm-track">Tracking: <strong>{result.trackingNumber || "—"}</strong></div>
              {result.totalCharge != null && (
                <div className="slm-cost">Cost: {result.currency || "USD"} {Number(result.totalCharge).toFixed(2)}</div>
              )}
              <div className="slm-success-actions">
                {result.labelUrl && (
                  <a className="slm-btn slm-btn-primary" href={result.labelUrl} target="_blank" rel="noreferrer">Download label</a>
                )}
                <button className="slm-btn slm-btn-danger" onClick={handleVoid} disabled={busy}>{busy ? "…" : "Void label"}</button>
              </div>
            </div>
          ) : (
            <>
              {error && <div className="slm-error">{error}</div>}

              <div className="slm-section">Ship To</div>
              <div className="slm-row">
                <label className="slm-grow"><span>Recipient / Company</span><input value={shipTo.name} onChange={(e) => setST("name", e.target.value)} /></label>
                <label><span>Phone</span><input value={shipTo.phone} onChange={(e) => setST("phone", e.target.value)} /></label>
              </div>
              <label className="slm-block"><span>Street Address</span><input value={shipTo.street} onChange={(e) => setST("street", e.target.value)} /></label>
              <div className="slm-row slm-row-3">
                <label className="slm-grow"><span>City</span><input value={shipTo.city} onChange={(e) => setST("city", e.target.value)} /></label>
                <label className="slm-sm"><span>State</span><input value={shipTo.state} maxLength={2} onChange={(e) => setST("state", e.target.value.toUpperCase())} /></label>
                <label className="slm-sm"><span>ZIP</span><input value={shipTo.postal} onChange={(e) => setST("postal", e.target.value)} /></label>
              </div>

              <div className="slm-section">Package & Service</div>
              <div className="slm-row slm-row-3">
                <label className="slm-grow">
                  <span>Service</span>
                  <select value={serviceCode} onChange={(e) => setServiceCode(e.target.value)}>
                    {SERVICE_CODES.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
                  </select>
                </label>
                <label className="slm-sm"><span>Weight (lbs)</span><input type="number" min="0" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} /></label>
                <label className="slm-sm">
                  <span>Label</span>
                  <select value={labelFormat} onChange={(e) => setLabelFormat(e.target.value)}>
                    <option value="PDF">PDF</option><option value="ZPL">ZPL</option><option value="GIF">GIF</option>
                  </select>
                </label>
              </div>
              <div className="slm-row slm-row-3">
                <label className="slm-sm"><span>Length (in)</span><input type="number" min="0" value={dims.length} onChange={(e) => setD("length", e.target.value)} /></label>
                <label className="slm-sm"><span>Width (in)</span><input type="number" min="0" value={dims.width} onChange={(e) => setD("width", e.target.value)} /></label>
                <label className="slm-sm"><span>Height (in)</span><input type="number" min="0" value={dims.height} onChange={(e) => setD("height", e.target.value)} /></label>
              </div>

              <label className="slm-check">
                <input
                  type="checkbox"
                  checked={markShipped && !shipGated}
                  disabled={shipGated}
                  onChange={(e) => setMarkShipped(e.target.checked)}
                />
                <span>Mark this order as Shipped</span>
              </label>
              {shipGated && <div className="slm-error">{shipBlockedMessage(order)}</div>}
            </>
          )}
        </div>

        <div className="slm-footer">
          {result ? (
            <button className="slm-btn" onClick={onClose}>Done</button>
          ) : (
            <>
              <button className="slm-btn" onClick={onClose} disabled={busy}>Cancel</button>
              {carrier?.enabled && carrier?.has_credentials && (
                <button className="slm-btn slm-btn-primary" onClick={handleCreate} disabled={busy || loadingCfg}>
                  {busy ? "Creating…" : "Create Label"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
